/**
 * Claro, escuro, ou o que o sistema operacional mandar.
 *
 * Três estados, e o terceiro é o padrão de propósito: o técnico que
 * configurou o tablet para escurecer às 18 h não deveria ter que
 * configurar de novo aqui. Só quem discorda do sistema é que escolhe.
 *
 * O estado escolhido vira `data-tema` no elemento raiz; "sistema" não
 * carimba nada e deixa a `@media (prefers-color-scheme)` decidir. É por
 * isso que a folha de estilo define a paleta escura DUAS vezes: uma sob a
 * media query, outra sob `[data-tema="escuro"]`. Definir só na media query
 * faria o botão não funcionar; só no atributo faria o padrão do sistema
 * não funcionar.
 */

export type Tema = 'claro' | 'escuro' | 'sistema';

const CHAVE = 'testesolo.tema';

export function lerTema(): Tema {
  try {
    const t = localStorage.getItem(CHAVE);
    if (t === 'claro' || t === 'escuro') return t;
  } catch { /* modo privado do navegador */ }
  return 'sistema';
}

export function aplicarTema(t: Tema): void {
  const raiz = document.documentElement;
  if (t === 'sistema') raiz.removeAttribute('data-tema');
  else raiz.setAttribute('data-tema', t);
  try {
    if (t === 'sistema') localStorage.removeItem(CHAVE);
    else localStorage.setItem(CHAVE, t);
  } catch { /* idem */ }
}

/** O próximo da roda: sistema → claro → escuro → sistema. */
export function proximoTema(t: Tema): Tema {
  return t === 'sistema' ? 'claro' : t === 'claro' ? 'escuro' : 'sistema';
}

export const NOME_TEMA: Record<Tema, string> = {
  sistema: 'Tema do sistema',
  claro: 'Tema claro',
  escuro: 'Tema escuro',
};

/*
 * ── O tema EFETIVO (onda 296, o motor do mapa) ──────────────────────
 *
 * A folha de estilo resolve «sistema» sozinha, pela media query. O mapa
 * não: o MapLibre desenha em WebGL, com um estilo claro OU escuro montado
 * em JavaScript (spec 05 §4.5), e precisa saber qual dos dois está valendo
 * — o atributo, se houver, senão o sistema operacional.
 */
export type TemaEfetivo = 'claro' | 'escuro';

const ESCURO_NO_SISTEMA = '(prefers-color-scheme: dark)';

export function temaEfetivo(): TemaEfetivo {
  const t = document.documentElement.getAttribute('data-tema');
  if (t === 'claro' || t === 'escuro') return t;
  return typeof window.matchMedia === 'function' && window.matchMedia(ESCURO_NO_SISTEMA).matches
    ? 'escuro' : 'claro';
}

/**
 * Avisa `fn` quando o tema efetivo muda: o botão da engrenagem troca o
 * `data-tema`, ou o sistema escurece às 18 h com o tema em «sistema». Só
 * avisa MUDANÇA (trocar «sistema» por «claro» num sistema claro não muda
 * nada que se veja). Devolve a função que desliga o aviso.
 */
export function aoMudarTemaEfetivo(fn: (t: TemaEfetivo) => void): () => void {
  let atual = temaEfetivo();
  const conferir = () => {
    const novo = temaEfetivo();
    if (novo !== atual) { atual = novo; fn(novo); }
  };
  const observador = new MutationObserver(conferir);
  observador.observe(document.documentElement, { attributes: true, attributeFilter: ['data-tema'] });
  const consulta = typeof window.matchMedia === 'function' ? window.matchMedia(ESCURO_NO_SISTEMA) : null;
  consulta?.addEventListener('change', conferir);
  return () => {
    observador.disconnect();
    consulta?.removeEventListener('change', conferir);
  };
}
